import '../css/app.css';
import './bootstrap';
import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import VueSocialSharing from 'vue-social-sharing';
import { ZiggyVue } from 'ziggy-js';
import { createInertiaApp } from '@inertiajs/vue3';
import filters from '@/filters';
import translatable from '@/mixins/translatable';
import { log } from '@/utils';
import { startAnalyticsIfAllowed } from '@/utils/use-cookie-consent';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { i18nVue } from 'laravel-vue-i18n';

const appName = window.document.title || 'Scaling Platform';

createInertiaApp({
	title: (title) =>
		title !== appName ? (title ? `${appName} | ${title}` : appName) : title,
	resolve: (name) => {
		const pages = import.meta.glob<DefineComponent>([
			'./Pages/*.vue',
			'./Pages/Frontend/**/*.vue',
			'./Pages/Auth/**/*.vue',
		]);
		return resolvePageComponent(`./Pages/${name}.vue`, pages);
	},
	setup({ el, App, props, plugin }) {
		const application = createApp({ render: () => h(App, props) })
			.use(plugin)
			.use(ZiggyVue)
			.use(i18nVue, {
				resolve: (lang: string) => {
					const languages = import.meta.glob('../../lang/*.json');
					return languages[`../../lang/${lang}.json`]?.();
				},
			})
			.use(VueSocialSharing)

			.mixin(translatable)
			.provide('log', log); // Composable for Vue 3 components

		application.config.globalProperties.log = log; // Global property for Vue 2 components
		application.config.globalProperties.$filters = filters;

		// Delete Page Props from DOM
		// https://github.com/inertiajs/inertia/discussions/695
		// https://github.com/inertiajs/inertia/discussions/372#discussioncomment-6052940
		if (import.meta.env.PROD) {
			delete (el as HTMLElement).dataset['page'];
		}

		application.mount(el);

		// GA/GTM only start once the stored cookie consent allows them.
		startAnalyticsIfAllowed();
	},
	progress: {
		// The delay after which the progress bar will appear, in milliseconds...
		delay: 250,
		// The color of the progress bar...
		color: '#29d',
		// Whether to include the default NProgress styles...
		includeCSS: true,
		// Whether the NProgress spinner will be shown...
		showSpinner: true,
	},
});
